import { FC, ReactElement, useState } from 'react';

import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './attachment-row.module.scss';

import {
  formatFileSize,
  getAttachmentPreviewCapability,
  getFileTypeCategory,
} from 'utils';
import { AttachmentPreviewModal } from 'views/components/UI/AttachmentPreview';
import { Button } from 'views/components/UI/Button';
import { FILE_TYPE_ICON } from 'views/components/UI/Icons/fileTypeIcons';
import { CloseSmall, Spinner } from 'views/components/UI/Icons/Icons';
import { StatusBadge, StatusBadgeVariant } from 'views/components/UI/StatusBadge';
import { Text } from 'views/components/UI/Text';

interface IAttachmentRowProps {
  name: string;
  size: number;
  contentType: string;
  badgeVariant: StatusBadgeVariant;
  badgeLabelId: string;
  isPending?: boolean;
  rejectionReasonLabelId?: string;
  previewFile?: File;
  requestDownloadUrl?: () => Promise<string>;
  onRemove: () => void;
}

const AttachmentRow: FC<IAttachmentRowProps> = ({
  name,
  size,
  contentType,
  badgeVariant,
  badgeLabelId,
  isPending,
  rejectionReasonLabelId,
  previewFile,
  requestDownloadUrl,
  onRemove,
}): ReactElement => {
  const intl = useIntl();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const previewCapability = getAttachmentPreviewCapability({
    contentType,
    fileName: name,
    sizeBytes: size,
  });
  const canPreviewAttachment =
    previewCapability.isPreviewable && Boolean(previewFile || requestDownloadUrl);
  const canOpenAttachment = canPreviewAttachment || Boolean(requestDownloadUrl);

  const handleAttachmentClick = async (): Promise<void> => {
    if (canPreviewAttachment) {
      setIsPreviewOpen(true);
      return;
    }

    if (!requestDownloadUrl) {
      return;
    }

    try {
      setIsDownloading(true);
      const downloadUrl = await requestDownloadUrl();
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  const category = getFileTypeCategory(contentType, name);
  const FileIcon = FILE_TYPE_ICON[category];

  const details = (
    <>
      <Text variant="bodySmall" className={styles.name}>
        {name}
      </Text>
      <span className={styles.size}>{formatFileSize(size)}</span>
    </>
  );

  return (
    <li className={styles.row}>
      {isPending && <Spinner className={styles.spinner} />}
      <span className={classNames(styles.iconBox, styles[`iconBox--${category}`])}>
        <FileIcon size="24px" />
      </span>
      {canOpenAttachment ? (
        <button
          type="button"
          className={styles.detailsButton}
          onClick={handleAttachmentClick}
          disabled={isDownloading}
        >
          {details}
        </button>
      ) : (
        <div className={styles.details}>{details}</div>
      )}
      <StatusBadge size="small" variant={badgeVariant}>
        <FormattedMessage id={badgeLabelId} />
      </StatusBadge>
      {rejectionReasonLabelId && (
        <span className={styles.reason}>
          <FormattedMessage id={rejectionReasonLabelId} />
        </span>
      )}
      <Button
        type="button"
        mode="tertiary"
        size="small"
        icon={<CloseSmall />}
        onClick={onRemove}
        aria-label={intl.formatMessage({ id: 'attachments.remove' })}
      />
      {canPreviewAttachment && (
        <AttachmentPreviewModal
          contentType={contentType}
          file={previewFile}
          fileName={name}
          handleClose={() => setIsPreviewOpen(false)}
          isOpen={isPreviewOpen}
          requestDownloadUrl={requestDownloadUrl}
          sizeBytes={size}
        />
      )}
    </li>
  );
};

export { AttachmentRow };
